// src/pages/TikTokAds.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../lib/authFetch';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge, Modal, ModalFooter, Input, Select, LastUpdated,
         Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui';
import { Bug, ChevronDown, ChevronRight } from 'lucide-react';
import { formatRelativeTime, cn } from '../lib/utils';
import {
  Plus, RefreshCw, Settings, Trash2, Check, BarChart3,
  LogIn, Database, ShoppingCart, Megaphone, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonAdPlatform } from '../components/ui/Skeleton';
import { useVisibilityRefresh } from '../hooks/useVisibilityRefresh';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar' }, { code: 'GBP', name: 'British Pound' },
  { code: 'EUR', name: 'Euro' },      { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'CAD', name: 'Canadian Dollar' }, { code: 'AUD', name: 'Australian Dollar' },
  { code: 'INR', name: 'Indian Rupee' },    { code: 'ZAR', name: 'South African Rand' },
];

// Split so the GMV Max columns can be dropped from the select when the
// migration adding them hasn't been applied yet.
const DAILY_BASE_COLS = 'date,spend,impressions,clicks';
const DAILY_GMV_COLS  = 'gmv_max_spend,gmv_max_orders,gmv_max_gross_revenue';

const EMPTY_FORM = { accountName: '', advertiserId: '', accessToken: '', currency: 'USD', exchangeRate: '1', taxRate: '0', showOnDashboard: true };

export default function TikTokAds() {
  const { user, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts]         = useState([]);
  const [campaigns, setCampaigns]       = useState([]);
  const [dailyData, setDailyData]       = useState([]);
  const [gmvMaxDaily, setGmvMaxDaily]   = useState([]);
  // True when the GMV Max migration is outstanding, so the page can say so
  // instead of just showing zeros.
  const [migrationPending, setMigrationPending] = useState(false);
  const [loading, setLoading]           = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  // Debug log captured from every TikTok API call.
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const appendLogs = (label, logs) => {
    if (!Array.isArray(logs) || logs.length === 0) return;
    const stamped = logs.map(l => `[${new Date().toLocaleTimeString()}] ${label} — ${l}`);
    setDebugLogs(prev => [...stamped, ...prev].slice(0, 200));
  };
  const [fetchingAccount, setFetchingAccount] = useState(null);
  const [fetchingAll, setFetchingAll]   = useState(false);
  const [connecting, setConnecting]     = useState(false);

  // Modals
  const [showManualModal, setShowManualModal]       = useState(false);
  const [showEditModal, setShowEditModal]           = useState(false);
  const [showDeleteModal, setShowDeleteModal]       = useState(false);
  const [showCampaignsModal, setShowCampaignsModal] = useState(false);
  const [showAdvertModal, setShowAdvertModal]       = useState(false); // advertiser picker after OAuth
  // "Add Account" asks how first, instead of putting two competing entry
  // points in the header.
  const [showAddChooser, setShowAddChooser]         = useState(false);

  // OAuth state
  const [availableAdvertisers, setAvailableAdvertisers] = useState([]);
  const [oauthToken, setOauthToken]                     = useState(null);
  const [selectingAdvert, setSelectingAdvert]           = useState(null); // which advertiser is being connected

  // Manual / edit form
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [isConnected, setIsConnected]   = useState(false);
  const [connectError, setConnectError] = useState('');
  const [loadedCampaigns, setLoadedCampaigns]   = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState({});
  const [modalStep, setModalStep]       = useState(1);
  const savedIdRef = useRef(null);

  // Manual entry
  const [manualForm, setManualForm] = useState({ accountId: '', date: new Date().toISOString().split('T')[0], spend: '', impressions: '', clicks: '' });

  const resetForm = () => { setFormData(EMPTY_FORM); setIsConnected(false); setConnectError(''); setLoadedCampaigns([]); setSelectedCampaigns({}); };

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user?.id) return;
    if (!user) return;
    try {
      const since = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

      let schemaPending = false;

      const [{ data: accs }, { data: camps }, dailyGmv, gmvRes] = await Promise.all([
        supabase.from('tiktok_accounts').select('*').eq('user_id', user.id).order('created_at'),
        supabase.from('tiktok_campaigns').select('*').eq('user_id', user.id),
        supabase.from('tiktok_daily')
          .select(`${DAILY_BASE_COLS},${DAILY_GMV_COLS}`)
          .eq('user_id', user.id).gte('date', since).order('date'),
        supabase.from('tiktok_gmv_max_daily')
          .select('date,tiktok_account_id,campaign_id,campaign_name,promotion_type,cost,orders,gross_revenue,roi')
          .eq('user_id', user.id).gte('date', since).order('date'),
      ]);

      // The GMV Max columns and the detail table only exist once
      // supabase/2026-07-29-tiktok-gmv-max.sql has been applied. PostgREST
      // rejects the whole select if any requested column is missing, so
      // without this fallback a pending migration blanks the entire page —
      // including auction spend, which has nothing to do with GMV Max.
      let daily = dailyGmv.data;
      if (dailyGmv.error) {
        schemaPending = true;
        const base = await supabase.from('tiktok_daily')
          .select(DAILY_BASE_COLS)
          .eq('user_id', user.id).gte('date', since).order('date');
        daily = base.data;
      }
      if (gmvRes.error) schemaPending = true;

      setMigrationPending(schemaPending);
      setAccounts(accs || []);
      setCampaigns(camps || []);
      setGmvMaxDaily(gmvRes.data || []);

      const byDate = {};
      for (const r of (daily || [])) {
        if (!byDate[r.date]) byDate[r.date] = { date: r.date, spend: 0, impressions: 0, clicks: 0, gmvSpend: 0, gmvOrders: 0, gmvRevenue: 0 };
        byDate[r.date].spend       += parseFloat(r.spend || 0);
        byDate[r.date].impressions += parseInt(r.impressions || 0);
        byDate[r.date].clicks      += parseInt(r.clicks || 0);
        byDate[r.date].gmvSpend    += parseFloat(r.gmv_max_spend || 0);
        byDate[r.date].gmvOrders   += parseInt(r.gmv_max_orders || 0);
        byDate[r.date].gmvRevenue  += parseFloat(r.gmv_max_gross_revenue || 0);
      }
      setDailyData(Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)));

      // Keep selectedAccount in sync
      setSelectedAccount(prev => {
        if (!prev && accs?.length) return accs[0];
        if (!prev) return null;
        return accs?.find(a => a.id === prev.id) || accs?.[0] || null;
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);
  useVisibilityRefresh(loadData);

  // ── Handle OAuth callback (?auth_code=xxx) ─────────────────────────────────
  useEffect(() => {
    const authCode = searchParams.get('auth_code');
    const state    = searchParams.get('state'); // contains userId
    if (!authCode || !user) return;

    setSearchParams({});
    setConnecting(true);
    const tid = toast.loading('Processing TikTok authorisation...');

    authFetch('/api/fetch-tiktok', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'callback', authCode, userId: user.id }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          toast.success(`Found ${data.advertisers?.length || 0} advertiser account(s)`, { id: tid });
          setOauthToken(data.access_token);
          setAvailableAdvertisers(data.advertisers || []);
          setShowAdvertModal(true);
        } else {
          toast.error(data.error || 'OAuth failed', { id: tid });
        }
      })
      .catch(e => toast.error(e.message, { id: tid }))
      .finally(() => setConnecting(false));
  }, [searchParams, user]);

  // ── Start OAuth ─────────────────────────────────────────────────────────────
  const handleOAuthConnect = async () => {
    setConnecting(true);
    try {
      const res  = await authFetch('/api/fetch-tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getAuthUrl', userId: user.id }) });
      const data = await res.json();
      if (data.success && data.authUrl) {
        sessionStorage.setItem('tiktok_connecting', 'true');
        window.location.href = data.authUrl;
      } else {
        toast.error(data.error || 'Could not get auth URL');
        setConnecting(false);
      }
    } catch (e) { toast.error(e.message); setConnecting(false); }
  };

  // ── Select advertiser after OAuth ──────────────────────────────────────────
  const handleSelectAdvertiser = async (advertiser) => {
    setSelectingAdvert(advertiser.id);
    const tid = toast.loading(`Connecting ${advertiser.name}...`);
    try {
      const res  = await authFetch('/api/fetch-tiktok', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'selectAdvertiser', userId: user.id, advertiserId: advertiser.id, accessToken: oauthToken, accountName: advertiser.name, currency: advertiser.currency }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Connected! ${data.campaignCount || 0} campaigns loaded`, { id: tid });
        await loadData();
      } else {
        toast.error(data.error || 'Connection failed', { id: tid });
      }
    } catch (e) { toast.error(e.message, { id: tid }); }
    setSelectingAdvert(null);
  };

  const handleDonePickingAdvertisers = () => {
    setShowAdvertModal(false);
    setAvailableAdvertisers([]);
    setOauthToken(null);
  };

  // ── Manual verify + add ────────────────────────────────────────────────────
  const handleVerifyManual = async () => {
    if (!formData.advertiserId.trim() || !formData.accessToken.trim()) { toast.error('Advertiser ID and Access Token required'); return; }
    setConnecting(true); setConnectError('');
    try {
      const res  = await authFetch('/api/fetch-tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'connect', advertiserId: formData.advertiserId.trim(), accessToken: formData.accessToken.trim() }) });
      const data = await res.json();
      if (!data.success) { setConnectError(data.error || 'Connection failed'); setConnecting(false); return; }
      setIsConnected(true);
      if (data.account?.name && !formData.accountName) setFormData(f => ({ ...f, accountName: data.account.name }));
      if (data.account?.currency) setFormData(f => ({ ...f, currency: data.account.currency }));
      const camps = data.campaigns || [];
      setLoadedCampaigns(camps);
      const sel = {}; camps.forEach(c => { sel[c.id] = true; });
      setSelectedCampaigns(sel);
      toast.success(`Connected! ${camps.length} campaigns found`);
    } catch (e) { setConnectError(e.message); }
    setConnecting(false);
  };

  const handleAddManualAccount = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.accountName.trim()) { toast.error('Account name required'); return; }
    if (!formData.advertiserId.trim()) { toast.error('Advertiser ID required'); return; }
    setSaving(true);
    try {
      const { data: acc, error } = await supabase.from('tiktok_accounts').insert({
        user_id: user.id, account_id: formData.advertiserId.trim(), account_name: formData.accountName.trim(),
        currency: formData.currency, exchange_rate: parseFloat(formData.exchangeRate) || 1, tax_rate: parseFloat(formData.taxRate) || 0,
        show_on_dashboard: formData.showOnDashboard, is_connected: false, api_enabled: false,
      }).select().single();
      if (error) { toast.error(error.code === '23505' ? 'Advertiser ID already exists' : error.message); setSaving(false); return; }
      savedIdRef.current = acc.id;
      toast.success('Account added! Connect API to fetch automatically.');
      setSaving(false); setModalStep(2);
    } catch (e) { toast.error(e.message); setSaving(false); }
  };

  const handleConnectManualApi = async () => {
    const accountDbId = savedIdRef.current;
    if (!accountDbId || !isConnected) { toast.error('Verify your token first'); return; }
    setSaving(true);
    try {
      await supabase.from('tiktok_accounts').update({ access_token: formData.accessToken.trim(), is_connected: true, api_enabled: true, updated_at: new Date().toISOString() }).eq('id', accountDbId);
      const campRows = loadedCampaigns.map(c => ({ user_id: user.id, tiktok_account_id: accountDbId, campaign_id: String(c.id), campaign_name: c.name, status: c.status, excluded: !selectedCampaigns[c.id] }));
      for (let i = 0; i < campRows.length; i += 50) await supabase.from('tiktok_campaigns').upsert(campRows.slice(i, i + 50), { onConflict: 'user_id,tiktok_account_id,campaign_id' });
      toast.success('API connected!');
      setShowManualModal(false); resetForm(); setModalStep(1); await loadData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const openEdit = (account) => {
    setSelectedAccount(account);
    setFormData({ accountName: account.account_name, advertiserId: account.account_id, accessToken: account.access_token || '', currency: account.currency || 'USD', exchangeRate: String(account.exchange_rate || 1), taxRate: String(account.tax_rate || 0), showOnDashboard: account.show_on_dashboard ?? true });
    setIsConnected(account.is_connected); setConnectError(''); setLoadedCampaigns([]); setSelectedCampaigns({});
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (saving || !selectedAccount) return;
    setSaving(true);
    try {
      const update = { account_name: formData.accountName.trim(), currency: formData.currency, exchange_rate: parseFloat(formData.exchangeRate) || 1, tax_rate: parseFloat(formData.taxRate) || 0, show_on_dashboard: formData.showOnDashboard, updated_at: new Date().toISOString() };
      const hasNewToken = formData.accessToken && formData.accessToken !== selectedAccount.access_token;
      if (hasNewToken && isConnected) { update.access_token = formData.accessToken.trim(); update.is_connected = true; update.api_enabled = true; }
      else if (!formData.accessToken && selectedAccount.access_token) { update.access_token = null; update.is_connected = false; update.api_enabled = false; }
      await supabase.from('tiktok_accounts').update(update).eq('id', selectedAccount.id);
      if (isConnected && loadedCampaigns.length > 0) {
        const campRows = loadedCampaigns.map(c => ({ user_id: user.id, tiktok_account_id: selectedAccount.id, campaign_id: String(c.id || c.campaign_id), campaign_name: c.name || c.campaign_name, status: c.status, excluded: !selectedCampaigns[c.id || c.campaign_id] }));
        for (let i = 0; i < campRows.length; i += 50) await supabase.from('tiktok_campaigns').upsert(campRows.slice(i, i + 50), { onConflict: 'user_id,tiktok_account_id,campaign_id' });
      }
      toast.success('Account updated'); setShowEditModal(false); resetForm(); await loadData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!selectedAccount || saving) return;
    setSaving(true);
    try {
      await supabase.from('tiktok_campaigns').delete().eq('tiktok_account_id', selectedAccount.id);
      await supabase.from('tiktok_daily').delete().eq('tiktok_account_id', selectedAccount.id);
      await supabase.from('tiktok_accounts').delete().eq('id', selectedAccount.id);
      toast.success('Account deleted'); setShowDeleteModal(false); setSelectedAccount(null); await loadData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Fetch spend ─────────────────────────────────────────────────────────────
  const handleFetch = async (account) => {
    if (!account.is_connected || !account.access_token) { toast.error('API not connected'); return; }
    setFetchingAccount(account.id);
    const tid = toast.loading(`Fetching ${account.account_name}...`);
    try {
      const excluded = campaigns.filter(c => c.tiktok_account_id === account.id && c.excluded).map(c => c.campaign_id);
      const res  = await authFetch('/api/fetch-tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'fetch', userId: user.id, tiktokAccountId: account.id, advertiserId: account.account_id, accessToken: account.access_token, exchangeRate: account.exchange_rate || 1, taxRate: account.tax_rate || 0, excludedCampaigns: excluded, debug: true }) });
      const data = await res.json();
      appendLogs(`fetch · ${account.account_name}`, data.logs);
      if (data.success) {
        const g = data.gmvMax;
        let msg = data.message || `Fetched ${data.records} days`;
        if (g?.error)          msg += ` · GMV Max unavailable`;
        else if (g?.rows > 0)  msg += ` · GMV Max $${Number(g.spend || 0).toFixed(2)} across ${g.campaigns} campaign${g.campaigns === 1 ? '' : 's'}`;
        else if (g?.available) msg += ` · no GMV Max activity`;
        toast.success(msg, { id: tid });
        if (g?.error) setDebugOpen(true);
        loadData();
      }
      else { toast.error(data.error || 'Fetch failed', { id: tid, duration: 8000 }); setDebugOpen(true); }
    } catch (e) {
      toast.error(e.message, { id: tid });
    }
    setFetchingAccount(null);
  };

  const handleFetchAll = async () => {
    const connected = accounts.filter(a => a.is_connected && a.access_token);
    if (!connected.length) { toast.error('No connected accounts'); return; }
    setFetchingAll(true);
    const tid = toast.loading(`Fetching ${connected.length} account(s)...`);
    let ok = 0; const errs = [];
    for (const a of connected) {
      try {
        const excluded = campaigns.filter(c => c.tiktok_account_id === a.id && c.excluded).map(c => c.campaign_id);
        const res  = await authFetch('/api/fetch-tiktok', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'fetch', userId: user.id, tiktokAccountId: a.id, advertiserId: a.account_id, accessToken: a.access_token, exchangeRate: a.exchange_rate || 1, taxRate: a.tax_rate || 0, excludedCampaigns: excluded, debug: true }) });
        const d    = await res.json();
        appendLogs(`fetch · ${a.account_name}`, d.logs);
        if (d.success) ok++; else errs.push(`${a.account_name}: ${d.error}`);
      } catch (e) { errs.push(`${a.account_name}: ${e.message}`); }
    }
    if (errs.length) { toast.error(`${ok} ok, ${errs.length} failed`, { id: tid }); setDebugOpen(true); }
    else toast.success(`Fetched ${ok} account(s)`, { id: tid });
    loadData(); setFetchingAll(false);
  };

  // ── Campaign toggle ─────────────────────────────────────────────────────────
  const toggleCampaignExclusion = async (camp) => {
    try {
      await supabase.from('tiktok_campaigns').update({ excluded: !camp.excluded }).eq('id', camp.id);
      toast.success(camp.excluded ? 'Campaign included' : 'Campaign excluded');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  // ── Ecommerce / Publisher toggle ───────────────────────────────────────────
  const toggleAccountFlag = async (account, flag) => {
    try {
      await supabase.from('tiktok_accounts').update({ [flag]: !account[flag] }).eq('id', account.id);
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  // Where this account's GMV Max spend is reported. Defaults to e-commerce —
  // GMV Max buys TikTok Shop orders, so its return is revenue, not clicks.
  const setGmvAttribution = async (account, value) => {
    const { error } = await supabase.from('tiktok_accounts')
      .update({ gmv_max_attribution: value }).eq('id', account.id);
    if (error) {
      // The column arrives with 2026-07-30-tiktok-gmv-max-attribution.sql.
      toast.error(
        /gmv_max_attribution|column/i.test(error.message)
          ? 'Run supabase/2026-07-30-tiktok-gmv-max-attribution.sql to enable this.'
          : error.message,
        { duration: 8000 },
      );
      return;
    }
    toast.success(
      value === 'ecommerce' ? 'GMV Max now counts toward E-commerce'
      : value === 'ads'     ? 'GMV Max now counts toward TikTok Ads'
      :                       'GMV Max now counts toward both',
    );
    loadData();
  };

  // ── Manual spend entry ──────────────────────────────────────────────────────
  const handleManualSpend = async (e) => {
    e.preventDefault();
    if (!manualForm.accountId || !manualForm.date || !manualForm.spend) { toast.error('Account, date and spend required'); return; }
    setSaving(true);
    try {
      const account = accounts.find(a => a.id === manualForm.accountId);
      const raw = parseFloat(manualForm.spend) || 0;
      const er  = parseFloat(account?.exchange_rate || 1);
      const tr  = parseFloat(account?.tax_rate || 0);
      const spendFinal = (er > 1 ? raw / er : raw * er) * (1 + tr / 100);
      const { error } = await supabase.from('tiktok_daily').upsert({ user_id: user.id, tiktok_account_id: manualForm.accountId, date: manualForm.date, spend: spendFinal, spend_original: raw, impressions: parseInt(manualForm.impressions || 0), clicks: parseInt(manualForm.clicks || 0), source: 'manual' }, { onConflict: 'user_id,tiktok_account_id,date' });
      if (error) throw error;
      toast.success('Entry saved'); setShowManualModal(false); setManualForm({ accountId: '', date: new Date().toISOString().split('T')[0], spend: '', impressions: '', clicks: '' }); loadData();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  // ── Stats ───────────────────────────────────────────────────────────────────
  const totalSpend  = dailyData.reduce((s, d) => s + d.spend, 0);
  const totalClicks = dailyData.reduce((s, d) => s + d.clicks, 0);
  const totalImpr   = dailyData.reduce((s, d) => s + d.impressions, 0);
  const avgCpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  const avgCtr = totalImpr   > 0 ? (totalClicks / totalImpr) * 100 : 0;

  // GMV Max — reported separately by TikTok (/gmv_max/report/get/), so it gets
  // its own totals. gmvSpend is already included in totalSpend above.
  const excludedCampaignIds = new Set(
    campaigns.filter(c => c.excluded).map(c => String(c.campaign_id)),
  );
  const includedGmvRows = gmvMaxDaily.filter(r => !excludedCampaignIds.has(String(r.campaign_id)));

  const gmvSpend   = includedGmvRows.reduce((s, r) => s + parseFloat(r.cost || 0), 0);
  const gmvOrders  = includedGmvRows.reduce((s, r) => s + parseInt(r.orders || 0), 0);
  const gmvRevenue = includedGmvRows.reduce((s, r) => s + parseFloat(r.gross_revenue || 0), 0);
  const gmvRoi     = gmvSpend > 0 ? gmvRevenue / gmvSpend : 0;
  const gmvCampaignCount = campaigns.filter(c => c.is_gmv_max).length;

  // GMV Max counts toward TikTok Ads only for accounts set to 'ads' or 'both'.
  // Default is e-commerce, so this is normally zero and the Ads tab shows
  // auction spend alone.
  const adsAttributedAccounts = new Set(
    accounts.filter(a => ['ads', 'both'].includes(a.gmv_max_attribution || 'ecommerce'))
            .map(a => a.id),
  );
  const gmvSpendForAds = includedGmvRows
    .filter(r => adsAttributedAccounts.has(r.tiktok_account_id))
    .reduce((sum, r) => sum + parseFloat(r.cost || 0), 0);
  const adsTotalSpend = totalSpend + gmvSpendForAds;

  // Per-campaign GMV Max breakdown over the window.
  // Per-day rollup, respecting the same campaign exclusions as everything else.
  const gmvDailyRows = Object.values(includedGmvRows.reduce((acc, r) => {
    if (!acc[r.date]) acc[r.date] = { date: r.date, cost: 0, orders: 0, revenue: 0 };
    acc[r.date].cost    += parseFloat(r.cost || 0);
    acc[r.date].orders  += parseInt(r.orders || 0);
    acc[r.date].revenue += parseFloat(r.gross_revenue || 0);
    return acc;
  }, {})).sort((a, b) => a.date.localeCompare(b.date));

  const gmvByCampaign = Object.values(includedGmvRows.reduce((acc, r) => {
    const k = r.campaign_id;
    if (!acc[k]) acc[k] = { campaign_id: k, campaign_name: r.campaign_name, promotion_type: r.promotion_type, cost: 0, orders: 0, gross_revenue: 0 };
    acc[k].cost          += parseFloat(r.cost || 0);
    acc[k].orders        += parseInt(r.orders || 0);
    acc[k].gross_revenue += parseFloat(r.gross_revenue || 0);
    return acc;
  }, {})).sort((a, b) => b.cost - a.cost);

  // Surfaced when TikTok refused the GMV Max endpoints (usually a missing
  // TikTok Shop / GMV Max scope on the developer app) — otherwise a real
  // permission problem is indistinguishable from "you have no GMV Max ads".
  const gmvErrors = accounts.filter(a => a.gmv_max_error);

  if (loading) return <SkeletonAdPlatform />;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><span>🎵</span> TikTok Ads</h1>
          <p className="text-muted-foreground text-sm">Track TikTok ad spend and ROAS</p>
          <LastUpdated
            value={accounts.reduce((latest, a) => {
              const t = a?.last_fetch || a?.updated_at;
              if (!t) return latest;
              return !latest || new Date(t) > new Date(latest) ? t : latest;
            }, null)}
            className="mt-2"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleFetchAll} disabled={fetchingAll}>
            <RefreshCw className={`h-4 w-4 mr-2 ${fetchingAll ? 'animate-spin' : ''}`} />
            {fetchingAll ? 'Fetching...' : 'Fetch All'}
          </Button>
          <Button variant="outline" onClick={() => { setModalStep(99); setShowManualModal(true); }}>Manual Entry</Button>
          <Button onClick={() => setShowAddChooser(true)} disabled={connecting}>
            {connecting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {connecting ? 'Connecting...' : 'Add Account'}
          </Button>
        </div>
      </div>

            {/* Stated on the page rather than only in the debug log, because the log
          needs a fetch to populate — and the symptom of the missing migration
          is a page full of zeros with nothing to click. */}
      {migrationPending && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <Database className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-600 dark:text-amber-400">
                Database migration pending
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                GMV Max needs columns this database doesn't have yet. Run{' '}
                <span className="font-mono">supabase/2026-07-29-tiktok-gmv-max.sql</span>{' '}
                in the Supabase SQL editor, then hit Fetch again. It's safe to re-run.
              </p>
              <p className="text-xs text-muted-foreground/80 mt-1">
                Standard ad spend is unaffected and shown below; only GMV Max figures are missing.
              </p>
            </div>
          </CardContent>
        </Card>
      )}


      <Tabs defaultValue="ads">
        <TabsList>
          <TabsTrigger value="ads">TikTok Ads</TabsTrigger>
          <TabsTrigger value="gmv">
            GMV Max{gmvCampaignCount > 0 ? ` (${gmvCampaignCount})` : ''}
          </TabsTrigger>
        </TabsList>

        {/* ── Standard auction advertising ── */}
        <TabsContent value="ads">
          <div className="space-y-6 mt-4">
          {/* Standard auction metrics. GMV Max is deliberately absent — it has its
              own tab, and its spend reports to E-commerce by default. */}
          {accounts.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { label: 'Ad Spend (30d)', value: `$${adsTotalSpend.toFixed(2)}` },
                { label: 'Clicks',         value: totalClicks.toLocaleString() },
                { label: 'Impressions',    value: totalImpr.toLocaleString() },
                { label: 'Avg CPC',        value: `$${avgCpc.toFixed(3)}` },
                { label: 'Avg CTR',        value: `${avgCtr.toFixed(2)}%` },
              ].map(s => (
                <Card key={s.label}><CardContent className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                  <p className="text-xl font-bold">{s.value}</p>
                </CardContent></Card>
              ))}
            </div>
          )}

          {accounts.length > 0 && adsTotalSpend === 0 && gmvSpend > 0 && (
            <Card className="border-border">
              <CardContent className="p-4 text-sm text-muted-foreground">
                No standard auction spend in this period — all of this account's spend came
                through GMV Max. See the <strong>GMV Max</strong> tab.
              </CardContent>
            </Card>
          )}
          {/* Spend chart */}
          {dailyData.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Spend — Last 30 Days</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v.toFixed(0)}`} />
                    <Tooltip formatter={v => [`$${Number(v).toFixed(2)}`]} />
                    <Legend />
                    <Line type="monotone" dataKey="spend" name="Spend" stroke="#fe2c55" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Daily data table */}
          {dailyData.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Daily Ad Spend</CardTitle>
                  {/* State the range actually covered. The window is what each fetch
                      retrieved, not the 30 days the old label promised — claiming
                      30 days while showing 4 reads as missing data. */}
                  <span className="text-xs text-muted-foreground">
                    {dailyData.length === 1
                      ? '1 day'
                      : `${dailyData.length} days (${new Date(dailyData[0].date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${new Date(dailyData[dailyData.length - 1].date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`}
                    {' · all accounts combined'}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        {['Date', 'Spend', 'Impressions', 'Clicks', 'CTR', 'CPC'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {[...dailyData].reverse().map(d => {
                        const ctr = d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0;
                        const cpc = d.clicks > 0 ? d.spend / d.clicks : 0;
                        return (
                          <tr key={d.date} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-foreground">
                              {new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 font-semibold text-red-500">${d.spend.toFixed(2)}</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.impressions.toLocaleString()}</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.clicks.toLocaleString()}</td>
                            <td className="px-4 py-3 text-muted-foreground">{ctr.toFixed(2)}%</td>
                            <td className="px-4 py-3 text-muted-foreground">${cpc.toFixed(3)}</td>
                          </tr>
                        );
                      })}
                      {/* Totals row */}
                      <tr className="bg-primary/5 font-semibold border-t-2 border-primary/20">
                        <td className="px-4 py-3 text-foreground/80">Total</td>
                        <td className="px-4 py-3 text-red-500">${totalSpend.toFixed(2)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{totalImpr.toLocaleString()}</td>
                        <td className="px-4 py-3 text-muted-foreground">{totalClicks.toLocaleString()}</td>
                        <td className="px-4 py-3 text-muted-foreground">{totalImpr > 0 ? ((totalClicks / totalImpr) * 100).toFixed(2) : '0.00'}%</td>
                        <td className="px-4 py-3 text-muted-foreground">${avgCpc.toFixed(3)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          </div>
        </TabsContent>

        {/* ── GMV Max: TikTok Shop automated campaigns ── */}
        <TabsContent value="gmv">
          <div className="space-y-6 mt-4">
          {/* ── GMV Max ──────────────────────────────────────────────────────────
              Reported by a separate TikTok API (/gmv_max/report/get/) with its own
              metric names — cost/orders/gross_revenue/ROI rather than
              spend/impressions/clicks — so it gets its own panel. */}
          {gmvErrors.length > 0 && (
            <Card className="border-amber-500/40 bg-amber-500/5">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-600 dark:text-amber-400">GMV Max data unavailable</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {gmvErrors.map(a => <li key={a.id}>· <strong>{a.account_name}</strong>: {a.gmv_max_error}</li>)}
                  </ul>
                  <p className="text-xs text-muted-foreground/80 mt-2">
                    GMV Max needs a TikTok Shop linked to the ad account, and the TikTok Shop
                    permissions granted to the app. Re-authorise via <strong>Add Account → Connect via TikTok API</strong> to refresh scopes.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {(gmvSpend > 0 || gmvCampaignCount > 0) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-rose-500" />
                    Performance
                  </CardTitle>
                  <span className="text-xs text-muted-foreground">
                    {gmvCampaignCount} campaign{gmvCampaignCount === 1 ? '' : 's'} · included in total spend above
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'GMV Max Spend',  value: `$${gmvSpend.toFixed(2)}` },
                    { label: 'Gross Revenue',  value: `$${gmvRevenue.toFixed(2)}` },
                    { label: 'Orders',         value: gmvOrders.toLocaleString() },
                    { label: 'ROI',            value: `${gmvRoi.toFixed(2)}x` },
                  ].map(s => (
                    <div key={s.label} className="rounded-lg border border-border p-3">
                      <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                      <p className="text-xl font-bold">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Pre-empts the obvious question: these are the only performance
                    metrics TikTok exposes for GMV Max at campaign level. */}
                <p className="text-xs text-muted-foreground">
                  TikTok reports GMV Max on cost, orders, revenue and ROI only — impressions,
                  clicks and CTR aren't available for these campaigns.
                </p>

                {gmvByCampaign.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          {['Campaign', 'Type', 'Spend', 'Orders', 'Gross Revenue', 'ROI'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {gmvByCampaign.map(c => (
                          <tr key={c.campaign_id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-3 py-2 font-medium">{c.campaign_name || c.campaign_id}</td>
                            <td className="px-3 py-2">
                              <Badge variant="secondary" className="text-[10px]">{c.promotion_type || '—'}</Badge>
                            </td>
                            <td className="px-3 py-2 font-semibold text-red-500">${c.cost.toFixed(2)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{c.orders.toLocaleString()}</td>
                            <td className="px-3 py-2 font-semibold text-emerald-600">${c.gross_revenue.toFixed(2)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{c.cost > 0 ? (c.gross_revenue / c.cost).toFixed(2) : '0.00'}x</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

            {gmvDailyRows.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle>Daily GMV Max</CardTitle>
                    <span className="text-xs text-muted-foreground">
                      {gmvDailyRows.length === 1 ? '1 day' : `${gmvDailyRows.length} days`} · all accounts combined
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40">
                        <tr>
                          {['Date', 'Spend', 'Orders', 'Gross Revenue', 'ROI', 'Cost / Order'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {[...gmvDailyRows].reverse().map(d => (
                          <tr key={d.date} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-foreground">
                              {new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 font-semibold text-red-500">${d.cost.toFixed(2)}</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.orders.toLocaleString()}</td>
                            <td className="px-4 py-3 font-semibold text-emerald-600">${d.revenue.toFixed(2)}</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.cost > 0 ? (d.revenue / d.cost).toFixed(2) : '0.00'}x</td>
                            <td className="px-4 py-3 text-muted-foreground">{d.orders > 0 ? `$${(d.cost / d.orders).toFixed(2)}` : '—'}</td>
                          </tr>
                        ))}
                        <tr className="bg-primary/5 font-semibold border-t-2 border-primary/20">
                          <td className="px-4 py-3 text-foreground/80">Total</td>
                          <td className="px-4 py-3 text-red-500">${gmvSpend.toFixed(2)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{gmvOrders.toLocaleString()}</td>
                          <td className="px-4 py-3 text-emerald-600">${gmvRevenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-muted-foreground">{gmvRoi.toFixed(2)}x</td>
                          <td className="px-4 py-3 text-muted-foreground">{gmvOrders > 0 ? `$${(gmvSpend / gmvOrders).toFixed(2)}` : '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {!gmvErrors.length && gmvSpend === 0 && gmvCampaignCount === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <ShoppingCart className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <h3 className="font-semibold mb-1">No GMV Max activity</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    GMV Max campaigns need a TikTok Shop linked to the ad account. Once they're
                    running, spend, orders, revenue and ROI appear here.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
      {/* Accounts */}
      {accounts.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <div className="text-5xl mb-4">🎵</div>
          <p className="font-semibold text-lg mb-2">No TikTok Ads accounts yet</p>
          <p className="text-muted-foreground text-sm mb-6">Connect via the TikTok API for automatic syncing, or add a manual account</p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Button onClick={() => setShowAddChooser(true)}><Plus className="h-4 w-4 mr-2" />Add Account</Button>
          </div>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Ad Accounts</h2>
          {accounts.map(account => {
            const accCamps   = campaigns.filter(c => c.tiktok_account_id === account.id);
            const excluded   = accCamps.filter(c => c.excluded).length;
            return (
              <Card key={account.id} className={`border-2 transition-colors ${selectedAccount?.id === account.id ? 'border-primary' : 'border-transparent hover:border-primary/30'}`}
                onClick={() => setSelectedAccount(account)}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    {/* Left: account info */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${account.is_connected ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{account.account_name}</p>
                          <Badge variant={account.is_connected ? 'default' : 'secondary'}>
                            {account.is_connected ? 'Connected' : 'Manual'}
                          </Badge>
                          {account.is_connected && accCamps.length > 0 && (
                            <Badge variant="outline">{accCamps.length - excluded}/{accCamps.length} campaigns</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          ID: {account.account_id} · {account.currency}
                          {account.tax_rate > 0 && ` · ${account.tax_rate}% tax`}
                          {account.exchange_rate !== 1 && ` · rate ${account.exchange_rate}`}
                          {account.last_fetch && ` · Synced ${formatRelativeTime(account.last_fetch)}`}
                        </p>

                        {/* Ecommerce / Publisher toggles */}
                        <div className="flex items-center gap-4 mt-2 flex-wrap" onClick={e => e.stopPropagation()}>
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                            <input type="checkbox" checked={!!account.use_for_ecommerce}
                              onChange={() => toggleAccountFlag(account, 'use_for_ecommerce')}
                              className="rounded" />
                            <ShoppingCart className="h-3 w-3 text-blue-600" />
                            <span>Ecommerce ROAS</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                            <input type="checkbox" checked={!!account.use_for_publisher}
                              onChange={() => toggleAccountFlag(account, 'use_for_publisher')}
                              className="rounded" />
                            <Megaphone className="h-3 w-3 text-green-600" />
                            <span>Publisher ROAS</span>
                          </label>
                        </div>

                        {/* GMV Max spend buys TikTok Shop orders, so it counts
                            as e-commerce cost of sale by default. Only shown
                            when the account actually has GMV Max activity —
                            otherwise it's a setting with nothing to govern. */}
                        {(account.gmv_max_store_ids?.length > 0
                          || campaigns.some(c => c.tiktok_account_id === account.id && c.is_gmv_max)) && (
                          <div className="flex items-center gap-2 mt-2 flex-wrap" onClick={e => e.stopPropagation()}>
                            <span className="text-xs text-muted-foreground">GMV Max spend counts toward</span>
                            <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
                              {[
                                { v: 'ecommerce', label: 'E-commerce' },
                                { v: 'ads',       label: 'TikTok Ads' },
                                { v: 'both',      label: 'Both' },
                              ].map(o => {
                                const current = account.gmv_max_attribution || 'ecommerce';
                                return (
                                  <button
                                    key={o.v}
                                    onClick={() => setGmvAttribution(account, o.v)}
                                    className={cn(
                                      'px-2 py-1 text-[11px] font-medium rounded-md transition-colors',
                                      current === o.v
                                        ? 'bg-card shadow-sm text-foreground'
                                        : 'text-muted-foreground hover:text-foreground',
                                    )}
                                  >
                                    {o.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: action buttons */}
                    <div className="flex items-center gap-2 flex-wrap flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {account.is_connected && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedAccount(account); setShowCampaignsModal(true); }}>
                            <BarChart3 className="h-3.5 w-3.5 mr-1" /> Campaigns
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleFetch(account)} disabled={fetchingAccount === account.id}>
                            {fetchingAccount === account.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(account)}><Settings className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="destructive" onClick={() => { setSelectedAccount(account); setShowDeleteModal(true); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── OAUTH ADVERTISER PICKER ── */}
      {/* ── Debug log panel ────────────────────────────────────────────────
          Surfaces what TikTok's API actually returned — including the
          campaign_type / objective_type breakdown the GMV Max diagnostic
          needs. Appears only after at least one API call has captured logs. */}
      {debugLogs.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => setDebugOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted/40"
          >
            <span className="flex items-center gap-1.5">
              <Bug className="h-3.5 w-3.5" />
              Debug log ({debugLogs.length} entr{debugLogs.length === 1 ? 'y' : 'ies'})
            </span>
            {debugOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          {debugOpen && (
            <div className="px-3 pb-3 pt-1 max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed space-y-0.5">
              {debugLogs.map((line, i) => (
                <div
                  key={i}
                  className={line.toLowerCase().includes('error') || line.toLowerCase().includes('fatal')
                    ? 'text-red-500'
                    : line.toLowerCase().includes('isgmvmax') || line.toLowerCase().includes('campaigns returned')
                      ? 'text-emerald-600'
                      : 'text-muted-foreground'}
                >
                  {line}
                </div>
              ))}
              <button onClick={() => setDebugLogs([])} className="text-xs text-primary hover:underline mt-2">Clear</button>
            </div>
          )}
        </div>
      )}

      {/* How do you want to add this account? Replaces the two competing
          header buttons with one entry point that explains the choice. */}
      <Modal
        isOpen={showAddChooser}
        onClose={() => setShowAddChooser(false)}
        title="Add TikTok Ad Account"
        description="Connect via the TikTok API for automatic syncing, or add a manual account to enter spend yourself."
      >
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => { setShowAddChooser(false); handleOAuthConnect(); }}
            disabled={connecting}
            className="w-full text-left rounded-xl border border-border hover:border-primary hover:bg-muted/40 transition-colors p-4 disabled:opacity-60"
          >
            <div className="flex items-start gap-3">
              <LogIn className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Connect via TikTok API</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sign in with TikTok and pick an advertiser. Spend, campaigns and GMV Max sync automatically.
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setShowAddChooser(false); resetForm(); setModalStep(1); setShowManualModal(true); }}
            className="w-full text-left rounded-xl border border-border hover:border-primary hover:bg-muted/40 transition-colors p-4"
          >
            <div className="flex items-start gap-3">
              <Database className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Add a manual account</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Track spend you enter yourself. You can connect the API to it later.
                </p>
              </div>
            </div>
          </button>
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => setShowAddChooser(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showAdvertModal} onClose={handleDonePickingAdvertisers}
        title="Select TikTok Advertiser Accounts"
        description="Choose which accounts to connect. You can connect multiple.">
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {availableAdvertisers.length === 0 && (
            <p className="text-center text-muted-foreground py-8 text-sm">No advertisers found on this TikTok account.</p>
          )}
          {availableAdvertisers.map(advert => {
            const alreadyConnected = accounts.some(a => a.account_id === advert.id);
            return (
              <div key={advert.id} className={`flex items-center justify-between p-4 rounded-xl border ${alreadyConnected ? 'border-green-500/30 bg-green-500/10' : 'border-border hover:border-primary'}`}>
                <div>
                  <p className="font-medium text-sm">{advert.name}</p>
                  <p className="text-xs text-muted-foreground">ID: {advert.id} · {advert.currency} · {advert.status}</p>
                </div>
                {alreadyConnected ? (
                  <span className="flex items-center gap-1 text-xs text-green-500 font-medium"><Check className="h-3.5 w-3.5" />Connected</span>
                ) : (
                  <Button size="sm" onClick={() => handleSelectAdvertiser(advert)} disabled={selectingAdvert === advert.id}>
                    {selectingAdvert === advert.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                    Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <ModalFooter>
          <Button onClick={handleDonePickingAdvertisers}>Done</Button>
        </ModalFooter>
      </Modal>

      {/* ── MANUAL ACCOUNT MODAL ── */}
      <Modal isOpen={showManualModal && modalStep !== 99} onClose={() => { setShowManualModal(false); resetForm(); setModalStep(1); }}
        title={modalStep === 1 ? 'Add TikTok Account (Manual)' : 'Connect API (Optional)'}
        description={modalStep === 1 ? 'Enter advertiser details manually' : 'Verify your access token to enable auto-fetch'}>
        {modalStep === 1 ? (
          <form onSubmit={handleAddManualAccount} className="space-y-4">
            <Input label="Account Name *" placeholder="My TikTok Ads" value={formData.accountName} onChange={e => setFormData(f => ({ ...f, accountName: e.target.value }))} required />
            <Input label="Advertiser ID *" placeholder="1234567890123" value={formData.advertiserId} onChange={e => setFormData(f => ({ ...f, advertiserId: e.target.value }))} hint="TikTok Ads Manager → Account info" required />
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium">Currency</label>
                <Select value={formData.currency} onChange={e => setFormData(f => ({ ...f, currency: e.target.value }))} className="mt-1">
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
                </Select>
              </div>
              <Input label="Exchange Rate" type="number" step="0.01" value={formData.exchangeRate} onChange={e => setFormData(f => ({ ...f, exchangeRate: e.target.value }))} hint="1 USD = X currency" />
            </div>
            <Input label="Tax Rate (%)" type="number" step="0.01" placeholder="0" value={formData.taxRate} onChange={e => setFormData(f => ({ ...f, taxRate: e.target.value }))} hint="VAT added to spend" />
            <ModalFooter>
              <Button variant="outline" type="button" onClick={() => { setShowManualModal(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" loading={saving}>Save & Continue</Button>
            </ModalFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-xs text-blue-400 dark:text-blue-300 space-y-1">
              <p className="font-semibold">Getting a TikTok Access Token</p>
              <p>1. TikTok for Business → My Apps → select your app</p>
              <p>2. Under "Long-term Token" generate a token with Ads Management scope</p>
              <p>3. Paste below — or use the <strong>Connect via TikTok</strong> button for easier OAuth login</p>
            </div>
            <Input label="Access Token" type="password" placeholder="your_long_lived_token" value={formData.accessToken} onChange={e => setFormData(f => ({ ...f, accessToken: e.target.value }))} />
            {connectError && <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg"><AlertCircle className="h-4 w-4 flex-shrink-0" /><span>{connectError}</span></div>}
            {isConnected && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-sm text-green-500 font-medium flex items-center gap-2"><Check className="h-4 w-4" /> Connected — {loadedCampaigns.length} campaigns</p>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                  {loadedCampaigns.map(c => (
                    <div key={c.id} onClick={() => setSelectedCampaigns(s => ({ ...s, [c.id]: !s[c.id] }))}
                      className="flex items-center gap-2 p-1.5 hover:bg-muted rounded cursor-pointer text-xs">
                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selectedCampaigns[c.id] ? 'bg-primary border-primary' : 'border-border'}`}>
                        {selectedCampaigns[c.id] && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="truncate flex-1">{c.name}</span>
                      <span className={`flex-shrink-0 ${c.status === 'ENABLE' ? 'text-green-600' : 'text-gray-400'}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <ModalFooter>
              <Button variant="outline" type="button" onClick={async () => { await supabase.from('tiktok_accounts').delete().eq('id', savedIdRef.current); setShowManualModal(false); resetForm(); setModalStep(1); }}>Cancel</Button>
              <Button variant="outline" onClick={handleVerifyManual} loading={connecting} disabled={!formData.accessToken.trim()}>{connecting ? 'Verifying...' : isConnected ? 'Re-verify' : 'Verify Token'}</Button>
              <Button onClick={handleConnectManualApi} loading={saving} disabled={!isConnected}>Save & Connect</Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* ── MANUAL SPEND ENTRY ── */}
      <Modal isOpen={showManualModal && modalStep === 99} onClose={() => { setShowManualModal(false); setModalStep(1); }} title="Manual Spend Entry">
        <form onSubmit={handleManualSpend} className="space-y-4">
          <div><label className="text-sm font-medium">Account *</label>
            <Select value={manualForm.accountId} onChange={e => setManualForm(f => ({ ...f, accountId: e.target.value }))} className="mt-1" required>
              <option value="">Select account...</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </Select>
          </div>
          <Input label="Date *" type="date" value={manualForm.date} onChange={e => setManualForm(f => ({ ...f, date: e.target.value }))} required />
          <Input label="Spend (account currency) *" type="number" step="0.01" placeholder="0.00" value={manualForm.spend} onChange={e => setManualForm(f => ({ ...f, spend: e.target.value }))} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Impressions" type="number" value={manualForm.impressions} onChange={e => setManualForm(f => ({ ...f, impressions: e.target.value }))} />
            <Input label="Clicks" type="number" value={manualForm.clicks} onChange={e => setManualForm(f => ({ ...f, clicks: e.target.value }))} />
          </div>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => { setShowManualModal(false); setModalStep(1); }}>Cancel</Button>
            <Button type="submit" loading={saving}>Save Entry</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── EDIT ACCOUNT ── */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); resetForm(); }} title={`Edit — ${selectedAccount?.account_name}`}>
        <form onSubmit={handleSaveEdit} className="space-y-4">
          <Input label="Account Name" value={formData.accountName} onChange={e => setFormData(f => ({ ...f, accountName: e.target.value }))} />
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">Currency</label>
              <Select value={formData.currency} onChange={e => setFormData(f => ({ ...f, currency: e.target.value }))} className="mt-1">
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
              </Select>
            </div>
            <Input label="Exchange Rate" type="number" step="0.01" value={formData.exchangeRate} onChange={e => setFormData(f => ({ ...f, exchangeRate: e.target.value }))} />
          </div>
          <Input label="Tax Rate (%)" type="number" step="0.01" value={formData.taxRate} onChange={e => setFormData(f => ({ ...f, taxRate: e.target.value }))} />
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-2">Access Token</p>
            <Input type="password" placeholder={selectedAccount?.access_token ? '••••••••' : 'No token set'} value={formData.accessToken} onChange={e => setFormData(f => ({ ...f, accessToken: e.target.value }))} />
            {formData.accessToken && <Button variant="outline" size="sm" type="button" className="mt-2" onClick={handleVerifyManual} loading={connecting}>{isConnected ? 'Re-verify' : 'Verify Token'}</Button>}
            {connectError && <p className="text-xs text-red-600 mt-1">{connectError}</p>}
            {isConnected && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><Check className="h-3 w-3" /> Verified — {loadedCampaigns.length} campaigns</p>}
          </div>
          <ModalFooter>
            <Button variant="outline" type="button" onClick={() => { setShowEditModal(false); resetForm(); }}>Cancel</Button>
            <Button type="submit" loading={saving}>Save Changes</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* ── CAMPAIGNS MODAL ── */}
      <CampaignsModal
        isOpen={showCampaignsModal}
        onClose={() => setShowCampaignsModal(false)}
        account={selectedAccount}
        campaigns={campaigns}
        onToggle={toggleCampaignExclusion}
        onRefresh={async () => {
          if (!selectedAccount?.is_connected) return;
          const tid = toast.loading('Refreshing campaigns…');
          try {
            const res = await authFetch('/api/fetch-tiktok', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'refreshCampaigns', userId: user.id, tiktokAccountId: selectedAccount.id, debug: true }),
            });
            const data = await res.json();
            appendLogs(`refreshCampaigns · ${selectedAccount.account_name}`, data.logs);
            if (data.success) {
              // data.count is what's in the database; data.fetched is what
              // TikTok returned. If TikTok had campaigns and none were stored,
              // the write failed — say so instead of reporting a cheerful zero.
              if (data.count === 0 && data.fetched > 0) {
                toast.error(
                  `TikTok returned ${data.fetched} campaigns but none could be saved — see the debug log.`,
                  { id: tid, duration: 10000 },
                );
                setDebugOpen(true);
              } else {
                toast.success(`Refreshed ${data.count} campaigns`, { id: tid });
              }
              loadData();
            } else {
              toast.error(data.error || 'Failed', { id: tid });
              setDebugOpen(true);
            }
          } catch (e) {
            toast.error(e.message, { id: tid });
          }
        }}
      />

      {/* ── DELETE ── */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Account" description="This removes the account and all its spend data. Cannot be undone.">
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} loading={saving}>Delete Account</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Campaigns modal — tabbed (All / GMV Max / Standard) with a diagnostic line
// when no GMV Max is found. Helps distinguish "account has none" from "TikTok
// is hiding them due to scope".
// ──────────────────────────────────────────────────────────────────────────────
function CampaignsModal({ isOpen, onClose, account, campaigns, onToggle, onRefresh }) {
  const [tab, setTab] = useState('all'); // 'all' | 'gmv_max' | 'standard'
  const all = campaigns.filter(c => c.tiktok_account_id === account?.id);

  // is_gmv_max is set from WHICH endpoint returned the campaign
  // (/gmv_max/campaign/get/ vs /campaign/get/), so it's authoritative. The
  // objective fallback only covers rows written before that flag existed —
  // 'PRODUCT_SALES' alone is not a GMV Max signal, plenty of ordinary shop
  // campaigns carry it too.
  const isGmv = (c) => c.is_gmv_max === true || (c.objective || '').toUpperCase() === 'GMV_MAX';
  const gmv = all.filter(isGmv);
  const std = all.filter(c => !isGmv(c));

  const visible = tab === 'gmv_max' ? gmv : tab === 'standard' ? std : all;

  // Diagnostic — when the GMV Max tab is empty, list the objective_types
  // that ARE present so the user can tell what TikTok returned.
  const objectiveCounts = all.reduce((acc, c) => {
    const k = c.objective || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <Modal isOpen={isOpen} onClose={onClose}
      title={`Campaigns — ${account?.account_name || ''}`}
      description="Uncheck to exclude a campaign from spend calculations">

      {/* Tab bar */}
      <div className="flex items-center gap-2 mb-3 border-b border-border pb-2 flex-wrap">
        {[
          { k: 'all',      label: 'All',         count: all.length },
          { k: 'gmv_max',  label: 'GMV Max',     count: gmv.length },
          { k: 'standard', label: 'Standard',    count: std.length },
        ].map(t => (
          <button key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              tab === t.k ? 'bg-primary text-white' : 'hover:bg-muted text-foreground'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded ${
              tab === t.k ? 'bg-white/20' : 'bg-muted text-muted-foreground'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="max-h-96 overflow-y-auto divide-y">
        {all.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            No campaigns yet. Click <strong>Fetch</strong> on the account card to load them.
          </p>
        ) : visible.length === 0 ? (
          <div className="py-6 text-sm space-y-2">
            <p className="text-muted-foreground">
              {tab === 'gmv_max'
                ? 'No GMV Max campaigns found on this account.'
                : 'No standard campaigns found on this account.'}
            </p>
            {tab === 'gmv_max' && (
              <div className="bg-muted/40 rounded-lg p-3 text-xs space-y-2">
                <div>
                  <p className="font-medium text-foreground mb-1">What was found:</p>
                  <ul className="space-y-0.5 text-muted-foreground">
                    {Object.entries(objectiveCounts).map(([k, v]) => (
                      <li key={k}>· <span className="font-mono">{k}</span> — {v} campaign{v === 1 ? '' : 's'}</li>
                    ))}
                  </ul>
                </div>
                {account?.gmv_max_error ? (
                  <p className="text-amber-600 dark:text-amber-400">
                    TikTok rejected the GMV Max lookup: <span className="font-mono">{account.gmv_max_error}</span>
                  </p>
                ) : (
                  <p className="text-muted-foreground/80">
                    GMV Max campaigns are read from TikTok's separate <span className="font-mono">/gmv_max/</span> endpoints,
                    which require a TikTok Shop linked to this ad account. If you have GMV Max ads but see none here,
                    check that the shop is attached and that the app has TikTok Shop permissions, then hit
                    <strong> Refresh Campaigns</strong>.
                  </p>
                )}
              </div>
            )}
          </div>
        ) : visible.map(camp => (
          <div key={camp.id} onClick={() => onToggle(camp)}
            className="flex items-center justify-between py-3 px-1 hover:bg-muted/40 cursor-pointer">
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center ${!camp.excluded ? 'bg-primary border-primary' : 'border-border'}`}>
                {!camp.excluded && <Check className="h-3 w-3 text-white" />}
              </div>
              <div>
                <p className={`text-sm font-medium flex items-start gap-1.5 flex-wrap ${camp.excluded ? 'line-through text-muted-foreground' : ''}`}>
                  <span className="min-w-0 break-words">{camp.campaign_name}</span>
                  {isGmv(camp) && (
                    <Badge variant="default" className="bg-rose-500 text-[10px] flex-shrink-0 whitespace-nowrap self-center">
                      GMV Max{camp.gmv_max_type ? ` · ${camp.gmv_max_type}` : ''}
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  ID: {camp.campaign_id} · {camp.status}
                  {camp.objective && ` · ${camp.objective}`}
                </p>
              </div>
            </div>
            <Badge variant={camp.excluded ? 'secondary' : 'default'}>{camp.excluded ? 'Excluded' : 'Included'}</Badge>
          </div>
        ))}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh Campaigns
        </Button>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
